/**
 * @fileoverview Shared callable API contract for `startTeachBackUpload`
 * (#5270), which creates the attempt a learner's uploaded Teach it back
 * video belongs to. The functions handler and the webapp client both import
 * it, so the two sides cannot drift in name, request shape, or response
 * shape.
 */
import {z} from 'zod';

import {FirestoreDocumentIdSchema} from '../firebase/types';
import type {BeginTeachBackAttemptRefusal} from './beginTeachBackAttemptApi';
import {
  TeachBackAttemptIdSchema,
  TeachBackSectionWeekSchema,
} from './teachBack';
import type {TeachBackLockReason} from './teachBackRules';

/**
 * Deployed Cloud Function name. Must match the exported function name in the
 * `sections` functions codebase.
 */
export const START_TEACH_BACK_UPLOAD_FUNCTION_NAME = 'startTeachBackUpload';

/**
 * What the client sends before it uploads the video to the attempt's
 * `teachBackRecordingPath`.
 */
export const StartTeachBackUploadRequestSchema = z.object({
  /** The course. */
  courseId: FirestoreDocumentIdSchema,
  /** The 1-based section week. */
  sectionWeek: TeachBackSectionWeekSchema,
  /**
   * One id per Upload click, reused by that click's retries; it becomes the
   * attempt's id.
   */
  requestId: TeachBackAttemptIdSchema,
  /** The learner's Super Section time slot. */
  superSectionId: FirestoreDocumentIdSchema,
});

/** Parsed request. */
export type StartTeachBackUploadRequest = z.infer<
  typeof StartTeachBackUploadRequestSchema
>;

/**
 * Why no upload was started: the same reasons a session start is refused,
 * since an upload uses one of the week's attempts.
 */
export type StartTeachBackUploadRefusal = BeginTeachBackAttemptRefusal;

/** What the callable returns. */
export type StartTeachBackUploadResponse =
  | {
      started: true;
      /** The attempt; the video goes to its `teachBackRecordingPath`. */
      attemptId: string;
      /** The spec the week is assigned. */
      teachBackId: string;
      /** Charged attempts, this upload included. */
      attemptsUsed: number;
      /** The spec's cap. */
      maxAttempts: number;
      /** The week's credited attempt after this upload. */
      creditedAttemptId: string;
    }
  | {
      started: false;
      /** Why not. */
      reason: StartTeachBackUploadRefusal;
      /** For `locked`: why. */
      lock?: TeachBackLockReason;
    };
